import { Component, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, info: React.ErrorInfo) => void
}

interface State {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.props.onError?.(error, info)
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info)
  }

  override render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
            <p className="font-display text-lg text-carbon">Something went wrong.</p>
            <p className="mt-2 text-sm text-carbon">Please refresh the page and try again.</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 bg-ink px-6 py-2.5 text-xs font-medium tracking-[0.12em] text-white transition-colors hover:bg-wd-orange"
            >
              REFRESH
            </button>
          </div>
        )
      )
    }

    return this.props.children
  }
}
